import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { TextField, AutoComplete, Select, DateTimePicker } from 'porosys-ui';
import moment from 'jalali-moment';
import yup from '../../../common/utils/yup';
import { getDepartments } from '../../../common/services/api/CommonApis/Department';
import { createMeeting, updateMeeting, getMeetingTitleTemplates } from '../api';
import { getUsersList } from '../../User/api';
import Form from '../../../common/ui/components/MainForm/MainForm';
import '../styles.scss';

const validationSchema = yup.object({
    title: yup.string().required(),
    titleTemplate: yup.string(),
    subject: yup.string(),
    participants: yup.array().required().min(2, 'حداقل دو نفر باید حضور داشته باشند').nullable(true),
    startAt: yup.string().required(),
    endAt: yup.string().required(),
    lead: yup.object().required().nullable(true),
    departments: yup.array().required().nullable(true),
    location: yup.string().required(),
});

const MeetingForm = props => {
    const { handleCancel, handleAddMeeting, handleUpdateMeeting, meetingToEdit } = props;

    const [participants, setParticipants] = useState([]);
    const [shouldEditData, setShouldEditData] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [meetingTitleTemplates, setMeetingTitleTemplates] = useState([]);

    useEffect(() => {
        const fetchParticipants = async () => {
            setParticipants(await getUsersList());
        };
        const fetchDepartments = async () => {
            setDepartments(await getDepartments());
        };
        const fetchMeetingTitleTemplates = async () => {
            setMeetingTitleTemplates(await getMeetingTitleTemplates());
        };

        fetchParticipants();
        fetchDepartments();
        fetchMeetingTitleTemplates();

        if (meetingToEdit) {
            setShouldEditData(true);
        }
    }, []);

    const inputValues = meetingToEdit
        ? {
              title: meetingToEdit.title,
              participants: meetingToEdit.participants.map(participant => {
                  return { label: participant.fullname, value: participant._id };
              }),
              subject: meetingToEdit.subject,
              startAt: meetingToEdit.startAt,
              endAt: meetingToEdit.endAt,
              lead: { value: meetingToEdit.lead._id, label: meetingToEdit.lead.fullname },
              departments: meetingToEdit.departments.map(department => {
                  return { label: department.label, value: department._id };
              }),
              location: meetingToEdit.location,
          }
        : {
              title: '',
              titleTemplate: '',
              subject: '',
              participants: [],
              startAt: '',
              endAt: '',
              lead: null,
              departments: [],
              location: '',
          };

    const { handleSubmit, values, handleChange, touched, errors, setFieldValue } = useFormik({
        initialValues: inputValues,
        onSubmit: values => {
            const data = { ...values };
            data.participants = values.participants.map(participant => participant.value);
            data.lead = values.lead.value;

            !shouldEditData
                ? createMeeting(data).then(res => {
                      handleAddMeeting(res);
                  })
                : updateMeeting(meetingToEdit._id, { ...data, _id: meetingToEdit._id }).then(res => {
                      handleUpdateMeeting(res);
                  });
        },
        validationSchema,
    });

    const handleError = state => {
        if (touched[state] && errors[state]) return errors[state];
    };

    const handleSetDate = (value, targetProp) => {
        setFieldValue(targetProp, moment(value).format('jYYYY/jMM/jDD HH:mm'));
    };

    const handleChangeTitleTemplates = e => {
        setFieldValue('titleTemplate', e);
        setFieldValue('title', e === '' ? '' : ' ');
    };

    return (
        <div>
            <Form
                formTitle={meetingToEdit ? `ویرایش جلسه ${meetingToEdit.title}` : `افزودن جلسه جدید`}
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
            >
                {!meetingToEdit && (
                    <Select
                        selected={values.titleTemplate}
                        classes={{ root: 'form-input' }}
                        label="انتخاب عنوان جلسه دوره ای"
                        onChange={handleChangeTitleTemplates}
                        options={meetingTitleTemplates.map(template => ({
                            label: template.title,
                            value: String(template._id),
                        }))}
                        placeholder="انتخاب قالب"
                        errorMessage={handleError('titleTemplate')}
                    />
                )}
                {!values.titleTemplate && (
                    <TextField
                        classes={{ root: 'form-input' }}
                        value={values.title}
                        label={'عنوان جلسه'}
                        name="title"
                        placeholder="وارد کنید..."
                        onChange={handleChange('title')}
                        errorMessage={handleError('title')}
                    />
                )}
                {/* <TextField
                    classes={{ root: 'form-input' }}
                    value={values.subject}
                    label={'موضوع جلسه'}
                    name="subject"
                    placeholder="وارد کنید..."
                    onChange={handleChange('subject')}
                    errorMessage={handleError('subject')}
                /> */}
                <AutoComplete
                    selected={values.participants}
                    classes={{ input: 'form-input' }}
                    label="حاضرین"
                    multiple
                    onChange={(e, newValue) => {
                        setFieldValue('participants', newValue);
                    }}
                    options={participants.map(user => {
                        return { value: user._id, label: user.fullname };
                    })}
                    placeholder="انتخاب کنید..."
                    errorMessage={handleError('participants')}
                />
                {values.participants && values.participants.length > 1 && (
                    <AutoComplete
                        selected={values.lead}
                        classes={{ input: 'form-input' }}
                        label="دبیر جلسه"
                        onChange={(e, newValue) => {
                            setFieldValue('lead', newValue);
                        }}
                        options={values.participants}
                        placeholder="انتخاب دبیر جلسه"
                        errorMessage={handleError('lead')}
                    />
                )}
                <DateTimePicker
                    defaultValue={
                        values.startAt !== '' ? moment.from(values.startAt, 'fa', 'YYYY-MM-DDTHH:mm:ss') : null
                    }
                    classes={{ root: 'form-input datepicker' }}
                    value={values.startAt}
                    label="شروع"
                    name="startAt"
                    placeholder="وارد کنید..."
                    onChange={e => {
                        handleSetDate(e, 'startAt');
                    }}
                    errorMessage={handleError('startAt')}
                />
                <DateTimePicker
                    defaultValue={values.endAt !== '' ? moment.from(values.endAt, 'fa', 'YYYY-MM-DDTHH:mm:ss') : null}
                    classes={{ root: 'form-input datepicker' }}
                    value={values.endAt}
                    label="پایان"
                    name="endAt"
                    placeholder="وارد کنید..."
                    onChange={e => {
                        handleSetDate(e, 'endAt');
                    }}
                    errorMessage={handleError('endAt')}
                />
                <AutoComplete
                    selected={values.departments}
                    classes={{ input: 'form-input' }}
                    label="واحد های مربوطه"
                    multiple
                    onChange={(e, newValue) => {
                        setFieldValue('departments', newValue);
                    }}
                    options={departments.map(department => {
                        return {
                            label: department.label,
                            value: department._id,
                        };
                    })}
                    placeholder="انتخاب کنید..."
                    errorMessage={handleError('departments')}
                />
                <Select
                    selected={values.location}
                    classes={{ root: 'form-input ' }}
                    label="محل برگزاری"
                    onChange={handleChange('location')}
                    options={[
                        {
                            label: 'کارخانه',
                            value: 'کارخانه',
                        },
                        {
                            label: 'دفتر مرکزی',
                            value: 'دفتر مرکزی',
                        },
                        {
                            label: 'آنلاین',
                            value: 'آنلاین',
                        },
                        {
                            label: 'سایر',
                            value: 'سایر',
                        },
                    ]}
                    placeholder="انتخاب مکان"
                    errorMessage={handleError('location')}
                />
            </Form>
        </div>
    );
};

export default MeetingForm;
